import { Box, Button, ButtonGroup, ButtonProps } from "@mui/material";
import { Business, ContactPhone, Email, Group, Place, Subject } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

export type VisitorBookingsColumnToggleId =
  | "visitorCount"
  | "externalCompany"
  | "externalEmail"
  | "externalContacts"
  | "externalVisitReason"
  | "location";

type Props = {
  visible: Record<VisitorBookingsColumnToggleId, boolean>;
  onToggle: (id: VisitorBookingsColumnToggleId) => void;
};

export function VisitorBookingsTableColumnToggles({ visible, onToggle }: Readonly<Props>) {
  const { t } = useTranslation();

  return (
    <Box
      data-testid="visitor-bookings-column-toggles"
      sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", px: 1, pb: 1 }}
    >
      <ButtonGroup size="small" variant="outlined" sx={{ flexWrap: "wrap" }}>
        <VisitorColumnToggleButton
          data-testid="visitor-bookings-toggle-visitor-count"
          onClick={() => onToggle("visitorCount")}
          visible={visible.visitorCount}
          buttonLabel={t("_visitorBookings_colVisitorCount")}
          startIcon={<Group />}
        />
        <VisitorColumnToggleButton
          data-testid="visitor-bookings-toggle-company"
          onClick={() => onToggle("externalCompany")}
          visible={visible.externalCompany}
          buttonLabel={t("_visitorBookings_colCompany")}
          startIcon={<Business />}
        />
        <VisitorColumnToggleButton
          data-testid="visitor-bookings-toggle-email"
          onClick={() => onToggle("externalEmail")}
          visible={visible.externalEmail}
          buttonLabel={t("_visitorBookings_colEmail")}
          startIcon={<Email />}
        />
        <VisitorColumnToggleButton
          data-testid="visitor-bookings-toggle-contacts"
          onClick={() => onToggle("externalContacts")}
          visible={visible.externalContacts}
          buttonLabel={t("_visitorBookings_colContact")}
          startIcon={<ContactPhone />}
        />
        <VisitorColumnToggleButton
          data-testid="visitor-bookings-toggle-visit-reason"
          onClick={() => onToggle("externalVisitReason")}
          visible={visible.externalVisitReason}
          buttonLabel={t("_visitorBookings_colVisitReason")}
          startIcon={<Subject />}
        />
        <VisitorColumnToggleButton
          data-testid="visitor-bookings-toggle-location"
          onClick={() => onToggle("location")}
          visible={visible.location}
          buttonLabel={t("_visitorBookings_colLocation")}
          startIcon={<Place />}
        />
      </ButtonGroup>
    </Box>
  );
}

type VisitorColumnToggleButtonProps = ButtonProps & {
  visible: boolean;
  startIcon: React.ReactNode;
  buttonLabel: string;
  onClick: () => void;
};

const VisitorColumnToggleButton = (props: VisitorColumnToggleButtonProps) => {
  const { visible, onClick, startIcon, buttonLabel, sx, ...rest } = props;
  const color = visible ? "primary" : "inherit";
  const variant = visible ? "contained" : "outlined";

  return (
    <Button
      {...rest}
      size="small"
      sx={{ flexWrap: "wrap", ...sx }}
      startIcon={startIcon}
      onClick={onClick}
      color={color}
      variant={variant}
    >
      {buttonLabel}
    </Button>
  );
};
